import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  /*
   * rounded-full в базе, а не в размерах.
   *
   * В системе нет ни одной кнопки с неполным скруглением, и держать это
   * правилом базы дешевле, чем повторять в каждом варианте: добавленный
   * позже размер получит таблетку автоматически.
   */
  /*
   * active:scale — отклик на нажатие.
   *
   * Его не было: кнопка меняла цвет по наведению и никак не отзывалась на
   * само нажатие. В приложении, где щёлкают сотни раз за смену, отсутствие
   * отклика читается как «не нажалось», и человек жмёт второй раз — а часть
   * действий здесь отправляет сообщения.
   *
   * 0.97, а не 0.95: при мелких иконочных кнопках более глубокое сжатие
   * выглядит скачком, а не нажатием.
   */
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-full text-sm font-medium whitespace-nowrap transition-all active:scale-[0.97] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        /*
         * Ghost-outline — основной парадный вариант референса: обводка и текст
         * брендом, фон — холст. Оставлен отдельным вариантом, а не заменил
         * собой default: в плотном рабочем интерфейсе главное действие обязано
         * читаться заливкой, иначе на панели из пяти кнопок неясно, какая из
         * них применяет, а какая отменяет.
         */
        brand:
          "border border-brand bg-background text-brand hover:bg-secondary hover:border-brand-hover",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-brand underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/*
 * forwardRef добавлен вручную, и это правка каталога, а не наш каприз.
 *
 * Компоненты shadcn сейчас генерируются под React 19, где ref — обычный проп
 * и forwardRef не нужен. Проект на React 18.3, где это не так: без обёртки
 * ссылка до элемента не доходит.
 *
 * Ломается это молча и не там, где ждёшь. Кнопка работает, но обёрнутая в
 * TooltipTrigger asChild она не отдаёт Radix ссылку на DOM-узел — подсказка
 * не находит якорь и позиционируется мимо. В консоли только предупреждение
 * «Function components cannot be given refs».
 *
 * При обновлении компонента через shadcn add правку придётся повторить.
 */
type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = "default",
    size = "default",
    asChild = false,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
});

export { Button, buttonVariants };
